import React, { useState } from 'react';
import { Alert, Button, notification, Row, Col } from 'antd';
import * as Yup from 'yup';
import { Formik } from 'formik';

import Input from '../../../common/components/dataEntry/formik/FormikInputField';
import { Counties, ZipCode } from '../../../types';
import config from '../../../config';
import API from '../../../api/api';
import Label from '../../../common/components/dataEntry/components/Label';

export const yourZipCodeDefaultValue = {
  zipCode: '',
};

const formValidator = Yup.object({
  zipCode: Yup.string().length(5, 'Invalid ZIP Code').required('Required.'),
});

export interface YourZipCodeProps {
  yourZipCodeInitialValues: ZipCode | null;
  onSubmitZipCode: (values: ZipCode) => void;
  getCounties: (value: Counties) => void;
  handleCollapse: () => void;
  enableNextAccordion: (value: boolean) => void;
}

const YourZipCode: React.FC<YourZipCodeProps> = ({
  yourZipCodeInitialValues,
  getCounties,
  onSubmitZipCode,
  handleCollapse,
  enableNextAccordion,
}) => {
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const initialFormValues: ZipCode =
    yourZipCodeInitialValues ?? yourZipCodeDefaultValue;

  const onSubmit = async (values: ZipCode) => {
    setLoading(true);
    await getCountiesByZip(values.zipCode);
    await onSubmitZipCode(values);
    setLoading(false);
  };

  const getCountiesByZip = async (zipCode: string) => {
    try {
      const response = await API.get(`counties/by/zip/${zipCode}`, {
        params: { apikey: config.API_KEY },
      });

      if (response.data.counties.length !== 0) {
        getMarketPlaceModel(response.data.counties);
      } else {
        setShowError(true);
        enableNextAccordion(false);
        setErrorText('You did not enter a valid ZIP Code');
      }
    } catch (error) {
      notification.open({
        type: 'error',
        message: 'There was an error',
      });
    }
  };

  const getMarketPlaceModel = async (counties: Counties[]) => {
    try {
      const response = await API.get(`states/${counties[0].state}`, {
        params: { apikey: config.API_KEY },
      });

      if (response.data.marketplace_model === ('FFM' || 'FFMPartnership')) {
        getCounties(counties[0]);
        setShowError(false);
        enableNextAccordion(true);
        handleCollapse();
      } else {
        enableNextAccordion(false);
        setShowError(true);
        setErrorText(
          "Oh no! It looks like you live in one of the states that uses its own health insurance exchange, which means we don't have their lowest cost silver plan rates available right now. If you are a PeopleKeep ICHRA user, please contact our friendly customer support team to get personalized help."
        );
      }
    } catch (error) {
      notification.open({
        type: 'error',
        message: 'There was an error',
      });
    }
  };
  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={initialFormValues}
        onSubmit={onSubmit}
        validationSchema={formValidator}
      >
        {(formik) => (
          <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Label className="formik-field__input-label no-select hand-on-hover ml-16 black mb-8">
              What is your home ZIP Code?
            </Label>
            <Row className="ml-16">
              <Col xs={12} sm={6} lg={4} xl={3}>
                <Input
                  type="number"
                  name="zipCode"
                  onChange={() => setShowError(false)}
                />
              </Col>
            </Row>
            <Row className="ml-16">
              <Col xs={12} lg={4} xl={3}>
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={loading}
                  className="mt-16 mb-16 button__large"
                  disabled={!formValidator.isValidSync(formik.values)}
                >
                  Next
                </Button>
              </Col>
            </Row>
          </form>
        )}
      </Formik>

      {showError && (
        <Alert
          className="ml-16"
          message=""
          description={errorText}
          type="error"
          showIcon={false}
        />
      )}
    </div>
  );
};
export default YourZipCode;
